import { useState, useEffect, useRef } from 'react'
import { useRecoilValue } from 'recoil'
import clsx from 'clsx'
import lottie from 'lottie-web'
import { isSystemMaintenanceValue } from '@/recoil/Network'
import { useWalletSelector } from '@/contexts/WalletSelectorContext'
import { loadData, saveData } from '@/utils/localStorage'
import './AppLoadingOverlay.css'

const ANIMATION_NAME = 'AppLoadingAnimation'
const ANIMATION_TIME = 200
const BETA_UNLOCK_KEY = 'betaUnlock'

export default function AppLoadingOverlay() {
  const { accountId, walletReady, isInsufficientBalance, initialError } =
    useWalletSelector()
  const isSystemMaintenance = useRecoilValue(isSystemMaintenanceValue)
  const container = useRef(null)
  const [needPassword, setNeedPassword] = useState(
    !loadData(BETA_UNLOCK_KEY) &&
      process.env.REACT_APP_DEFAULT_NETWORK === 'disabled'
  )
  const [show, setShow] = useState(true)
  const [active, setActive] = useState(true)

  useEffect(() => {
    if (
      (!isSystemMaintenance && !accountId && !needPassword) ||
      isInsufficientBalance
    ) {
      setShow(false)
      setActive(false)
      return
    }

    setShow(true)
    setActive(true)
  }, [isSystemMaintenance, accountId, needPassword, isInsufficientBalance])

  useEffect(() => {
    if (lottie.getRegisteredAnimations().length > 0) {
      return
    }

    active &&
      lottie.loadAnimation({
        name: ANIMATION_NAME,
        container: container.current,
        path: '/static/loading-animation.json?2023081001',
        renderer: 'svg',
        loop: true,
        autoplay: true
      })
  }, [active])

  useEffect(() => {
    if (isSystemMaintenance || !accountId || !walletReady || needPassword) {
      return
    }

    setActive(false)
    const timer = setTimeout(() => {
      setShow(false)
      lottie.destroy(ANIMATION_NAME)
    }, ANIMATION_TIME)
    return () => clearTimeout(timer)
  }, [isSystemMaintenance, accountId, walletReady, needPassword])

  const onPasswordInput = e => {
    if (String(e.target.value).toLowerCase() === 'octopus') {
      saveData(BETA_UNLOCK_KEY, true)
      setNeedPassword(false)
    }
  }

  return (
    <div
      className={clsx('popup-card-overlay app-loading-overlay noselect', {
        show,
        active
      })}
    >
      <div ref={container} className="animation-container" />
      {needPassword && (
        <input
          className="secret-password-input"
          placeholder="Password?"
          onChange={onPasswordInput}
        />
      )}
      {initialError && (
        <div className="error-info">
          Error occurred during connecting wallet 😵‍💫
          <br />
          <br />
          {initialError.initialProcessStatus}
          <br />
          <code>{`${initialError.error.name}: ${initialError.error.message}`}</code>
        </div>
      )}
      {isSystemMaintenance && (
        <div className="system-maintenance-info">
          System is under maintenance
        </div>
      )}
    </div>
  )
}
